import {
	CheckIcon,
	EditIcon,
	EditSquareIcon,
	LaunchIcon,
	MailIcon,
	PauseIcon,
	ReleaseIcon,
	StopIcon,
	Text,
	VisibleIcon,
} from '@viapeople/components'
import { addEdge, type Connection, type Edge } from '@xyflow/react'
import { v7 as uuidv7 } from 'uuid'
import { type CustomEdge, StepType } from './types'

export const formatStepType = (stepType: string): string => {
	return stepType
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ')
}

export const createCustomEdge = (params: Connection): CustomEdge => {
	return {
		...params,
		id: uuidv7(),
		source: params.source || '',
		target: params.target || '',
		type: 'customEdge',
		data: {
			label: 'Connection',
			description: '',
			onDelete: () => {},
		},
	}
}

export const connectNodes = (params: Connection, edges: Edge[]): CustomEdge[] => {
	const newEdge = createCustomEdge(params)
	return addEdge(newEdge, edges) as CustomEdge[]
}

export interface CycleStepDiagramAction {
	key: string
	movesForward: boolean
	allowedNextSteps: string[] | null
}

export interface CycleStepDiagramDefinition {
	[stepKey: string]: {
		possibleActions: CycleStepDiagramAction[]
	}
}

export const getEdgeData = (
	sourceStepType: string,
	targetStepType: string,
	diagramDefinition: CycleStepDiagramDefinition,
) => {
	const sourceStepActions = diagramDefinition[sourceStepType]?.possibleActions || []

	const matchingAction = sourceStepActions.find((action) => {
		if (action.allowedNextSteps === null) return true

		return action.allowedNextSteps?.includes(targetStepType)
	})

	if (!matchingAction) return null

	return {
		key: matchingAction.key,
		label: formatStepType(matchingAction.key),
		movesForward: matchingAction.movesForward,
		allowedNextSteps: matchingAction.allowedNextSteps,
	}
}

interface StepMetadataItem {
	icon: React.ReactNode
	labels: Record<string, string>
	title: string
	customParticipantsTitle?: React.ReactNode
	group?: boolean
	defineParticipants?: boolean
	defineReleasers?: boolean
	defineHoldEditors?: boolean
	defineRaterListApprover?: boolean
	email?: boolean
	emailSetup?: boolean
	showRequiredCheckboxes?: boolean
	showAllowCAEdit?: boolean
	showReviewerEdit?: boolean
	showRequiredRadioGroup?: boolean
}

export type StepMetadataType = {
	[K in StepType]: StepMetadataItem
}

export const stepMetadata: StepMetadataType = {
	[StepType.CYCLE_START]: {
		icon: <LaunchIcon />,
		title: 'Cycle Start',
		labels: {
			approve: 'Start of process',
		},
	},
	[StepType.EDIT_REVIEWERS_LIST]: {
		icon: <EditIcon />,
		labels: {
			approve: 'On select reviewers',
		},
		title: 'Edit reviewer list',
		defineParticipants: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.APPROVE_REVIEWERS_LIST]: {
		icon: <VisibleIcon />,
		labels: {
			approve: 'On approving reviewers',
			reject: 'On rejecting reviewers',
		},
		title: 'Approve reviewer list',
		customParticipantsTitle: (
			<>
				Filter participants
				<br />
				<Text variant='xsmall'>
					This step will create tasks to the participant's manager or cycle admin, but the filter is based on the
					participants
				</Text>
			</>
		),
		defineParticipants: true,
		defineRaterListApprover: true,
		showRequiredCheckboxes: true,
		showReviewerEdit: true,
		email: true,
	},
	[StepType.SELF_EVALUATION]: {
		icon: <EditSquareIcon />,
		labels: {
			approve: 'On submitting self evalutation',
		},
		title: 'Self evaluation',
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.GROUP_EVALUATION]: {
		icon: <EditSquareIcon />,
		labels: {
			approve: 'On submitting group evaluation',
		},
		title: 'Group Evaluation',
		customParticipantsTitle: (
			<>
				Filter participants
				<br />
				<Text variant='xsmall'>
					This step will create tasks to the reviewers selected for each group, but the filter is based on the
					participants being evaluated
				</Text>
			</>
		),
		group: true,
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.MANAGER_EVALUATION]: {
		icon: <EditSquareIcon />,
		labels: {
			approve: 'On submitting manager evalutation',
			reject: 'On rejecting self evaluation',
		},
		title: 'Manager Evaluation',
		customParticipantsTitle: (
			<>
				Filter participants
				<br />
				<Text variant='xsmall'>
					This step will create tasks to the participant's manager, but the filter is based on the participants
				</Text>
			</>
		),
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		showRequiredRadioGroup: true,
		email: true,
	},
	[StepType.UNSOLICITED_EVALUATION]: {
		icon: <EditSquareIcon />,
		labels: {
			approve: 'On submitting unsolicited evalutation',
		},
		title: 'Unsolicited Evaluation',
		customParticipantsTitle: (
			<>
				Filter participants
				<br />
				<Text variant='xsmall'>
					This step will create tasks to all unsolicited reviewers according to cycle settingss, but the filter is based
					on the participants being evaluated
				</Text>
			</>
		),
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.DIRECT_REPORTS_EVALUATION]: {
		icon: <EditSquareIcon />,
		labels: {
			approve: 'On submitting direct reports evaluation',
		},
		title: 'Direct Reports Evaluation',
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.PEER_EVALUATION]: {
		icon: <EditSquareIcon />,
		labels: {
			approve: 'On submitting peer evaluation',
		},
		title: 'Peer Evaluation',
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.DOWN_EVALUATION]: {
		icon: <EditSquareIcon />,
		labels: {
			approve: 'On submitting down evaluation',
		},
		title: 'Down Evaluation',
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.UP_EVALUATION]: {
		icon: <EditSquareIcon />,
		labels: {
			approve: 'On submitting up evaluation',
		},
		title: 'Up Evaluation',
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.CALIBRATION]: {
		icon: <CheckIcon />,
		labels: {
			approve: 'On completing calibration',
		},
		title: 'Calibration',
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.REVIEW]: {
		icon: <CheckIcon />,
		labels: {
			approve: 'On approving manager evalutation',
			reject: 'On rejecting manager evaluation',
		},
		title: 'Review',
		customParticipantsTitle: (
			<>
				Filter participants
				<br />
				<Text variant='xsmall'>
					This step will create tasks to the cycle admins, but the filter is based on the participants
				</Text>
			</>
		),
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.RELEASE]: {
		icon: <ReleaseIcon />,
		labels: {
			approve: 'On release evalutation',
		},
		title: 'Release',
		customParticipantsTitle: (
			<>
				Filter participants
				<br />
				<Text variant='xsmall'>
					This step will create tasks to the participant's manager, but the filter is based on the participants
				</Text>
			</>
		),
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.ACKNOWLEDGE]: {
		icon: <CheckIcon />,
		labels: {
			approve: 'On acknowledgment completed',
		},
		title: 'Acknowledge',
		defineParticipants: true,
		showAllowCAEdit: true,
		showRequiredCheckboxes: true,
		email: true,
	},
	[StepType.EMAIL]: {
		icon: <MailIcon />,
		labels: {
			approve: 'On email sent',
		},
		title: 'Email',
		defineParticipants: true,
		emailSetup: true,
	},
	[StepType.HOLD]: {
		icon: <PauseIcon />,
		labels: {
			approve: 'On hold released',
		},
		title: 'Hold',
		defineReleasers: true,
		defineHoldEditors: true,
		email: true,
	},
	[StepType.CYCLE_END]: {
		icon: <StopIcon />,
		labels: {
			approve: 'End of process',
		},
		title: 'Cycle End',
	},
}

// Only export step types that should appear in the palette
// Exclude internal/backend-only step types that aren't ready for UI use
export const stepTypes = (Object.keys(stepMetadata) as StepType[]).filter(
	(stepType) =>
		![
			StepType.DIRECT_REPORTS_EVALUATION,
			StepType.PEER_EVALUATION,
			StepType.DOWN_EVALUATION,
			StepType.UP_EVALUATION,
			StepType.CALIBRATION,
		].includes(stepType),
)
